import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { SpacingValues } from "@/lib/utils/SpacingValues";
import { usePriceDetailsRoute } from "@/features/price-calculator/hooks/UseRoute";
import { PriceCalculatorAuditTab } from "@/features/price-calculator/components/PriceCalculatorAuditTab";
import { transformCustomerResultToAuditData } from "@/features/price-calculator/lib/result-panel-transformers";
import { PriceCalculatorMultiLegResultTab } from "@/features/price-calculator/components/PriceCalculatorMultiLegResultTab";
import { PriceCalculatorSectionalResultTab } from "@/features/price-calculator/components/PriceCalculatorSectionalResultTab";
import {
  PriceCalculatorContext,
  PriceCalculatorContextType,
} from "@/features/price-calculator/PriceCalculatorContext";
import {
  MovementType,
  SelectedTab,
} from "@/features/price-calculator/types/types";
import { Box, Row, Txt } from "@stenajs-webui/core";
import {
  Banner,
  Card,
  CardBody,
  PrimaryButton,
  Tab,
  TabMenu,
} from "@stenajs-webui/elements";
import React, { useContext, useState } from "react";
import { IndentValues } from "@/lib/utils/IndentValues";

interface Props {
  onCalculatePrice: (useCustomerInvoiceCurrency: boolean) => void;
}

export const PriceCalculatorCustomerSearchResultPane = ({
  onCalculatePrice,
}: Props) => {
  const { customerCalculatedState, customerResult } = useContext(
    PriceCalculatorContext
  ) as PriceCalculatorContextType;

  const { error: routeError, data: routeData } = usePriceDetailsRoute(
    customerCalculatedState.routeCode
  );
  const routeName = routeData?.productPrice.route.byId?.name;

  const priceTabDisabled = customerResult?.priceCalculatorData.some(
    (data) => data.totalPrice === 0
  );

  const [selectedTab, setSelectedTab] = useState<SelectedTab>(
    priceTabDisabled ? SelectedTab.AUDIT : SelectedTab.PRICE
  );

  const displaySectionalDetailsTab =
    selectedTab === SelectedTab.PRICE &&
    customerCalculatedState.movementType === MovementType.SECTIONAL;

  const displayMultiLegDetailsTab =
    selectedTab === SelectedTab.PRICE &&
    customerCalculatedState.movementType === MovementType.MULTILEG;

  const convertedFrom = customerResult?.priceCalculatorData[0].convertedFrom;

  return (
    <Card>
      <CardBody>
        <TabMenu>
          <Tab
            disabled={priceTabDisabled}
            label={"Price details"}
            selected={selectedTab === SelectedTab.PRICE}
            onClick={() => setSelectedTab(SelectedTab.PRICE)}
            data-testid={testIdConstants.priceCalculatorPriceDetailsButton}
          />
          <Tab
            label={"Audit track"}
            selected={selectedTab === SelectedTab.AUDIT}
            onClick={() => setSelectedTab(SelectedTab.AUDIT)}
            data-testid={testIdConstants.priceCalculatorAuditTrackButton}
          />
        </TabMenu>
        {routeError && (
          <Box spacing={SpacingValues.TWENTY}>
            <Banner
              headerText={"Could not load route"}
              text={"Tried to load error, but there was a problem"}
              variant={"error"}
            />
          </Box>
        )}
      </CardBody>
      {customerResult?.priceCalculatorData && routeName && (
        <>
          {displaySectionalDetailsTab && (
            <PriceCalculatorSectionalResultTab
              customerCalculatedState={customerCalculatedState}
              customerResult={customerResult}
              routeName={routeName}
            />
          )}
          {displayMultiLegDetailsTab && (
            <PriceCalculatorMultiLegResultTab
              customerCalculatedState={customerCalculatedState}
              customerResult={customerResult}
            />
          )}
          {selectedTab === SelectedTab.AUDIT && (
            <PriceCalculatorAuditTab
              auditData={customerResult.priceCalculatorData.map((priceCalc) =>
                transformCustomerResultToAuditData(
                  priceCalc,
                  customerCalculatedState
                )
              )}
            />
          )}

          {!!convertedFrom && (
            <Row
              spacing={IndentValues.SIXTEEN}
              indent={IndentValues.SIXTEEN}
              gap={IndentValues.SIXTEEN}
              justifyContent={"space-between"}
              alignItems="center"
            >
              <Txt>The prices has been converted to invoice currency</Txt>
              <PrimaryButton
                onClick={() => onCalculatePrice(false)}
                label="Recalculate in agreement currency"
              />
            </Row>
          )}
        </>
      )}
    </Card>
  );
};
